"use client";

import dynamic from "next/dynamic";
import { Suspense } from "react";
import { SessionProvider } from "next-auth/react";
import { Toaster } from "sonner";
import { GenerationProvider } from "@/lib/generation-context";
import { PostHogProvider } from "@/components/analytics/posthog-provider";

/**
 * Global floating UI — neither widget is critical for first paint and
 * they conditionally render based on session / generation state. Lazy-
 * loading with `ssr: false` keeps their JS out of the initial bundle
 * AND skips them during SSR so the HTML payload stays leaner. Their
 * chunks fetch after hydration completes.
 */
const FeedbackButton = dynamic(
  () =>
    import("@/components/shared/feedback-button").then(
      (m) => m.FeedbackButton,
    ),
  { ssr: false, loading: () => null },
);

const GenerationMiniPlayer = dynamic(
  () =>
    import("@/components/shared/generation-mini-player").then(
      (m) => m.GenerationMiniPlayer,
    ),
  { ssr: false, loading: () => null },
);

// Floating help launcher (bottom-left). Logged-in only for now (the component
// self-gates on session); lazy + ssr:false keeps its JS out of first paint.
const HelpButton = dynamic(
  () => import("@/components/help/help-button").then((m) => m.HelpButton),
  { ssr: false, loading: () => null },
);

/**
 * Cookie banner is client-only and reads document.cookie on mount.
 * SSR rendering would either flash the banner for consented users or
 * mismatch hydration; ssr:false avoids both.
 */
const CookieBanner = dynamic(
  () =>
    import("@/components/marketing/cookie-banner").then((m) => m.CookieBanner),
  { ssr: false, loading: () => null },
);

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      {/* PostHog must sit INSIDE SessionProvider so AuthIdentifier
          can read the session. Suspense wraps PostHogProvider because
          useSearchParams (used inside PageViewTracker) requires it
          under the App Router. */}
      <Suspense fallback={null}>
        <PostHogProvider>
          <GenerationProvider>
            {children}
            <GenerationMiniPlayer />
            <FeedbackButton />
            <HelpButton />
            <CookieBanner />
            {/* Toaster mounts at the root so any AsyncButton click
                anywhere in the tree can emit toasts. Position:
                bottom-right matches the FeedbackButton corner; theme
                inherits the dark navy via the CSS variables already
                set on `<html>`. */}
            <Toaster
              position="bottom-right"
              theme="dark"
              richColors
              closeButton
              toastOptions={{ duration: 4000 }}
            />
          </GenerationProvider>
        </PostHogProvider>
      </Suspense>
    </SessionProvider>
  );
}
